// src/components/AutocompleteSelect.js
import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'antd';
import '../assets/scss/AutocompleteSelect.scss';

const AutocompleteSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder = 'Select an option', 
  disabled = false,
  displayProperty = 'value',
  valueProperty = 'value'
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const autocompleteRef = useRef(null);

  // Initialize search input with the selected value's display text
  useEffect(() => {
    if (value) {
      const selectedOption = options.find(option => option[valueProperty] === value);
      if (selectedOption) {
        setSearchInput(selectedOption[displayProperty]);
      }
    } else {
      setSearchInput('');
    }
  }, [value, options, displayProperty, valueProperty]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowDropdown(false);
        
        // Reset the input to match the current value
        if (value) {
          const selectedOption = options.find(option => option[valueProperty] === value);
          if (selectedOption) {
            setSearchInput(selectedOption[displayProperty]);
          }
        } else {
          setSearchInput('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [value, options, displayProperty, valueProperty]);

  // Filter options based on search input
  const filteredOptions = options.filter(option => 
    option[displayProperty].toLowerCase().includes(searchInput.toLowerCase())
  );

  const handleInputChange = (e) => {
    setSearchInput(e.target.value);
    setShowDropdown(true);
    
    // If input is cleared, also clear the selected value
    if (e.target.value === '') {
      onChange('');
    }
  };

  const handleOptionSelect = (option) => {
    onChange(option[valueProperty]);
    setSearchInput(option[displayProperty]);
    setShowDropdown(false);
  };

  return (
    <div className="autocomplete-select-container" ref={autocompleteRef}>
      <Input
        type="text"
        placeholder={placeholder}
        value={searchInput}
        onChange={handleInputChange}
        onClick={() => !disabled && setShowDropdown(true)}
        disabled={disabled}
        autoComplete="off"
      />
      
      {showDropdown && !disabled && (
        <div className="autocomplete-select-dropdown">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div 
                key={index} 
                className="autocomplete-select-item"
                onClick={() => handleOptionSelect(option)}
              >
                {option[displayProperty]}
              </div>
            ))
          ) : (
            <div className="autocomplete-select-item text-muted">No matches found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteSelect;
